// App operations: loading data via the worker, saving settings, file export, toasts.
import * as E from "../core/engine";
import type { ApiConfigResult, ApiData, ApiInstrumentResult, ApiMarket, ApiRequest, ImportReport, PortfelConfig } from "../data/types";
import type { DataSource } from "../worker/protocol";
import { ComputeClient } from "../worker/client";
import { app } from "./state.svelte";
import { store } from "./store";

let client: ComputeClient | null = null;

interface ApiRoutes { "/api/data": ApiData; "/api/market": ApiMarket; "/api/import": ImportReport; "/api/config": ApiConfigResult; "/api/instrument": ApiInstrumentResult }
type Route = keyof ApiRoutes;

/** Request to the "in-browser server" (the worker reads and writes the data folder). */
export async function api<P extends Route>(path: P | `${P}?${string}`, opts?: ApiRequest): Promise<ApiRoutes[P]> {
  if (!client) throw new Error("Nie wybrano folderu z danymi");
  const transfer = opts && opts.body instanceof ArrayBuffer ? [opts.body] : [];
  return (await client.call("api", { path, opts }, transfer)) as ApiRoutes[P];
}

let toastTimer: ReturnType<typeof setTimeout> | undefined;
export function toast(msg: string, ms = 3500): void {
  app.toast = msg;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => (app.toast = null), ms);
}

export function download(name: string, text: string, mime = "text/csv;charset=utf-8"): void {
  const blob = new Blob(["﻿" + text], { type: mime });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 500);
}
export interface CsvCol<T> { k?: string; label?: string; v?: (r: T) => unknown }
export function toCsv<T>(rows: readonly T[], cols: CsvCol<T>[]): string {
  const q = (v: unknown) => { const s = v === null || v === undefined ? "" : String(v); return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  return [cols.map((c) => q(c.label || c.k)).join(","), ...rows.map((r) => cols.map((c) => q(typeof c.v === "function" ? c.v(r) : (r as Record<string, unknown>)[c.k as string])).join(","))].join("\n");
}

// ------------------------------------------------------------------ loading
let poll: ReturnType<typeof setTimeout> | undefined;
let pollN = 0;

export async function loadAll({ refreshMarket = false, marketOnly = false } = {}): Promise<void> {
  app.busy = true;
  try {
    if (!marketOnly || !app.data) {
      const data = await api("/api/data");
      app.data = data;
      for (const r of data.inbox || []) toast("error" in r ? `Inbox: ${r.file} — błąd: ${r.error}` : `Zaimportowano z inbox: ${r.file} (${r.accounts.map((a) => `${a.account} +${a.new}`).join(", ")})`, 6000);
      const cfg = data.config || {};
      if (app.tax === null) app.tax = store.get("tax", cfg.belka?.enabled !== false);
    }
    app.status = "Pobieranie notowań i kursów NBP…";
    try {
      const market = await api(refreshMarket ? "/api/market?refresh=1" : "/api/market");
      app.market = market;
      app.marketError = null;
      // the worker fetches quotes in the background — poll every few seconds until it finishes
      clearTimeout(poll);
      if (market.refreshing) {
        pollN++;
        if (pollN < 40) poll = setTimeout(() => loadAll({ marketOnly: true }), 6000);
      } else if (pollN) { pollN = 0; toast("Notowania zaktualizowane"); }
    } catch (e) {
      app.market = app.market || ({ prices: {}, fx: {}, errors: [] } as unknown as ApiMarket);
      app.marketError = (e as Error).message;
    }
    await rebuild();
  } finally {
    app.busy = false;
  }
}

export async function rebuild(): Promise<void> {
  const cfg = app.data?.config || {};
  // the worker builds the model (from the data it loaded itself); here we only add the accessor functions
  const data = await (client as ComputeClient).call("model", { opts: { taxRate: +(cfg.belka?.rate ?? 0.19), settlementDays: +(cfg.belka?.settlementDays ?? 0),
    taxExempt: Object.entries(cfg.accounts || {}).filter(([, v]) => v && v.taxExempt).map(([k]) => k), today: E.todayStr() } });
  const model = E.hydrateModel(data);
  const accs = model.accounts;
  // "All" also covers accounts that appeared after an import (saved as null,
  // or — in the current session — when all known accounts were selected before the rebuild)
  const wasAll = !!app.model && app.model.accounts.every((a) => app.accounts.has(a));
  const saved = wasAll ? null : store.get<string[] | null>("accounts", null);
  const keep = (saved || accs).filter((a) => accs.includes(a));
  app.model = model;
  app.accounts = new Set(keep.length ? keep : accs);
  app.status = null;
  app.screen = "app";
}

/** Save config.json and refresh the views that use the configuration. */
export async function saveConfig(cfg: PortfelConfig): Promise<void> {
  await api("/api/config", { method: "POST", body: JSON.stringify(cfg) });
  if (app.data) app.data = { ...app.data, config: cfg };
}

// ------------------------------------------------------------------ filters
export function setAccounts(s: Set<string>): void {
  app.accounts = s;
  const all = !!app.model && app.model.accounts.every((a) => s.has(a));
  store.set("accounts", all ? null : [...s]);
}
export function setPeriod(k: typeof app.period): void {
  app.period = k;
  store.set("period", k);
}
export function setCustom(c: { start: string; end: string }): void {
  app.custom = c;
  store.set("custom", c);
}
export function setTax(v: boolean): void {
  app.tax = v;
  store.set("tax", v);
}
export function setPrivacy(v: boolean): void {
  app.privacy = v;
  store.set("privacy", v);
}
export function toggleTheme(): void {
  const cur = document.documentElement.dataset.theme || (matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
  const next = cur === "dark" ? "light" : "dark";
  document.documentElement.dataset.theme = next;
  store.set("theme", next);
  app.theme++;
}
export function refreshMarket(): void {
  loadAll({ refreshMarket: true, marketOnly: true }).then(() => toast("Pobieram najnowsze notowania w tle…"));
}

export const openInstrument = (key: string): void => { app.drawerKey = key; };
export const closeDrawer = (): void => { app.drawerKey = null; };

// ------------------------------------------------------------------ start: choosing the data folder
export async function startBrowser(source: DataSource): Promise<void> {
  client ||= new ComputeClient();
  // make the proxy URL absolute: inside the worker, relative URLs resolve against the worker file (/assets/…), not the page
  const proxy = new URL(store.get("proxy", "proxy"), location.href).href;
  app.fs = await client.call("init", { source, proxy });
  app.status = "Wczytywanie danych z folderu…";
  app.loadingMsg = "Wczytywanie danych z folderu…";
  app.screen = "loading";
  await loadAll();
}
export const demoSource = (): DataSource => ({ kind: "demo", base: new URL("demo/", location.href).href });

export function showWelcome(err = ""): void {
  app.welcomeError = err;
  app.status = "";
  app.screen = "welcome";
}

export async function boot(): Promise<void> {
  const q = new URLSearchParams(location.search);
  if (q.has("demo")) {
    try { return await startBrowser(demoSource()); } catch (e) { console.error(e); }
  }
  showWelcome();
}
